import { useEffect, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from "@chakra-ui/react";
import PlacingStatusBadge from "./PlacingStatusBadge";
import PartyName from "./PartyName";
import NotesField from "./NotesField";
import { ReasonSelect, ReasonText } from "./ReasonFields";
import { useReasonCodes } from "../../hooks/useReasonCodes";
import { declineQuoteRequest, getQuote, sendQuote } from "../../services/PlacingAPI";
import { showApiErrorToast } from "../../utils/showApiToast";
import {
  formatDate,
  formatShareRange,
  isInFuture,
  isPast,
  readParticipation,
  shareFields,
  toIsoUtc,
  validateShares,
} from "./placingUtils";

// The carrier can quote (again) while the request is open
const QUOTABLE_STATUSES = ["sent", "quoted"];

const Field = ({ label, children }) => (
  <Box>
    <Text fontSize="xs" color="gray.500">{label}</Text>
    <Box fontSize="sm">{children}</Box>
  </Box>
);

const OUTCOMES = {
  nominated: { status: "success", title: "You've been nominated as lead", text: "The broker chose your quote." },
  ntu: { status: "warning", title: "Not taken up", text: "The broker nominated another lead." },
  declined: { status: "error", title: "You declined to quote", text: null },
  withdrawn: { status: "info", title: "Withdrawn by the broker", text: null },
};

const emptyQuote = {
  quotedShare: "",
  minimumShare: "",
  maximumShare: "",
  reference: "",
  validUntilDate: "",
  subjectivities: "",
  stampReference: "",
  participationBasis: "",
  currency: "",
  notes: "",
};

const asInput = (n) => (n == null ? "" : String(n));

/** A new quote's form, with the shares pre-filled from what the broker asked for. */
const quoteFormFor = (quoteRequest) => {
  const asked = readParticipation(quoteRequest);
  return {
    ...emptyQuote,
    quotedShare: asInput(asked.requestedShare),
    minimumShare: asInput(asked.minimumShare),
    maximumShare: asInput(asked.maximumShare),
  };
};

// The request lists its quotes as { value: quoteId }
const quoteIdOf = (q) => (typeof q === "string" ? q : q?.value || q?.quoteId || q?._id);

/** Carrier view of a quote request: stamp and send quotes, or decline to quote (step 10). */
const CarrierQuoteRequestView = ({ quoteRequest, onChanged }) => {
  const toast = useToast();
  const [quotes, setQuotes] = useState([]);
  const [form, setForm] = useState(() => quoteFormFor(quoteRequest));
  const [sending, setSending] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonCode, setReasonCode] = useState("");
  const { isOther } = useReasonCodes();
  const [declining, setDeclining] = useState(false);

  const quoteIds = (quoteRequest.quotes || []).map(quoteIdOf).filter(Boolean);
  const quoteIdsKey = quoteIds.join(",");
  const canQuote = QUOTABLE_STATUSES.includes(quoteRequest.status);
  const canDecline = quoteRequest.status === "sent" && quoteIds.length === 0;
  const terms = readParticipation(quoteRequest);
  const overdue =
    quoteRequest.overdue ?? (quoteRequest.status === "sent" && isPast(quoteRequest.responseRequiredByDate));
  const declineReason = quoteRequest.receiver?.declineReason || quoteRequest.declineReason;
  const outcome = OUTCOMES[quoteRequest.status];

  useEffect(() => {
    let cancelled = false;
    const ids = quoteIdsKey ? quoteIdsKey.split(",") : [];
    Promise.all(ids.map((id) => getQuote(id)))
      .then((list) => !cancelled && setQuotes(list))
      .catch((err) => !cancelled && showApiErrorToast(toast, err, { title: "Couldn't load your quotes" }));
    return () => {
      cancelled = true;
    };
  }, [quoteIdsKey, toast]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const stampAndSend = async () => {
    const shareError = validateShares(
      { share: form.quotedShare, minimumShare: form.minimumShare, maximumShare: form.maximumShare },
      { shareRequired: false }
    );
    if (shareError) return toast({ title: shareError, status: "warning" });
    if (!isInFuture(form.validUntilDate)) {
      return toast({ title: "Valid until must be a date in the future", status: "warning" });
    }

    setSending(true);
    try {
      await sendQuote(quoteRequest._id, {
        ...shareFields({
          quotedShare: form.quotedShare,
          minimumShare: form.minimumShare,
          maximumShare: form.maximumShare,
        }),
        validUntilDate: toIsoUtc(form.validUntilDate),
        ...(form.notes.trim() && { notes: form.notes.trim() }),
        ...(form.reference && { reference: form.reference }),
        ...(form.subjectivities && { subjectivities: form.subjectivities }),
        ...(form.stampReference && { stampReference: form.stampReference }),
        ...(form.participationBasis && { participationBasis: form.participationBasis }),
        ...(form.currency && { currency: form.currency }),
      });
      toast({ title: "Quote stamped and sent", status: "success" });
      setForm(quoteFormFor(quoteRequest));
      await onChanged();
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't send quote" });
    } finally {
      setSending(false);
    }
  };

  const decline = async () => {
    if (!reasonCode) return toast({ title: "Choose a reason for declining", status: "warning" });
    if (isOther(reasonCode) && !reason.trim()) return toast({ title: "Explain the reason (you chose other)", status: "warning" });
    if (!window.confirm("Decline to quote? You can't quote on this request afterwards.")) return;
    setDeclining(true);
    try {
      await declineQuoteRequest(quoteRequest._id, { reasonCode, reason: reason.trim() });
      toast({ title: "Declined to quote", status: "success" });
      setReason("");
      setReasonCode("");
      await onChanged();
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't decline" });
    } finally {
      setDeclining(false);
    }
  };

  return (
    <VStack align="stretch" spacing={6}>
      {outcome && (
        <Alert status={outcome.status} variant="left-accent" fontSize="md" py={4}>
          <AlertIcon boxSize="6" />
          <Box>
            <AlertTitle fontSize="lg">{outcome.title}</AlertTitle>
            {quoteRequest.status === "declined" ? (
              <ReasonText fontSize="md" code={quoteRequest.receiver?.reasonCode} notes={declineReason} />
            ) : quoteRequest.status === "ntu" ? (
              <>
                <AlertDescription>{outcome.text}</AlertDescription>
                <ReasonText fontSize="sm" code={quoteRequest.ntuReasonCode} notes={quoteRequest.ntuNotes} />
              </>
            ) : (
              (outcome.text || declineReason) && <AlertDescription>{outcome.text || declineReason}</AlertDescription>
            )}
          </Box>
        </Alert>
      )}

      <Box borderWidth="1px" borderRadius="md" p={4} bg="whiteAlpha.800">
        <HStack justify="space-between" mb={3}>
          <Heading size="md">Quote request</Heading>
          <PlacingStatusBadge status={quoteRequest.status} fontSize="md" />
        </HStack>
        <SimpleGrid columns={[1, 2, 4]} spacing={3}>
          <Field label="Broker">
            <PartyName partyId={quoteRequest.sender?.partyId} />
          </Field>
          <Field label="To">
            <PartyName partyId={quoteRequest.receiver?.partyId} />
          </Field>
          <Field label="Requested share">
            {terms.requestedShare != null ? `${terms.requestedShare}%` : "—"}
          </Field>
          <Field label="Range">{formatShareRange(terms.minimumShare, terms.maximumShare) || "—"}</Field>
          <Field label="Participation basis">{terms.participationBasis || "—"}</Field>
          <Field label="Currency">{terms.currency || "—"}</Field>
          <Field label="Response required by">
            {formatDate(quoteRequest.responseRequiredByDate) || "—"}
            {overdue && <Badge ml={2} colorScheme="red">OVERDUE</Badge>}
          </Field>
        </SimpleGrid>
        {quoteRequest.notes && (
          <Box mt={3}>
            <Text fontSize="xs" color="gray.500">Notes from the broker</Text>
            <Text fontSize="sm" whiteSpace="pre-wrap">{quoteRequest.notes}</Text>
          </Box>
        )}
      </Box>

      <Box borderWidth="1px" borderRadius="md" p={4} bg="whiteAlpha.800">
        <Heading size="sm" mb={3}>Your quotes</Heading>
        {quotes.length === 0 ? (
          <Text fontSize="sm" color="gray.500">You haven't sent a quote yet.</Text>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Status</Th><Th>Reference</Th><Th>Stamp</Th><Th>Share</Th><Th>Range</Th>
                <Th>Currency</Th><Th>Valid until</Th><Th>Subjectivities</Th><Th>Quote detail</Th><Th>Stamped</Th>
              </Tr>
            </Thead>
            <Tbody>
              {quotes.map((q) => {
                const qTerms = readParticipation(q);
                const expired = q.expired ?? (q.status === "sent" && isPast(q.validUntilDate));
                return (
                  <Tr key={q._id || q.quoteId} bg={q.status === "nominated" ? "green.50" : undefined}>
                    <Td>
                      <PlacingStatusBadge status={q.status} />
                      {q.status === "ntu" && <ReasonText color="orange.700" mt={1} code={q.ntuReasonCode} notes={q.ntuNotes} />}
                    </Td>
                    <Td>{q.reference || "—"}</Td>
                    <Td>{q.stampReference || "—"}</Td>
                    <Td>{qTerms.quotedShare != null ? `${qTerms.quotedShare}%` : "—"}</Td>
                    <Td>{formatShareRange(qTerms.minimumShare, qTerms.maximumShare)}</Td>
                    <Td>{qTerms.currency || ""}</Td>
                    <Td>
                      {formatDate(q.validUntilDate)}
                      {expired && <Badge ml={2} colorScheme="red">EXPIRED</Badge>}
                    </Td>
                    {/* The quote record keeps subjectivities as lineConditionsDescription */}
                    <Td maxW="260px" whiteSpace="pre-wrap">{q.subjectivities || q.lineConditionsDescription || ""}</Td>
                    <Td maxW="320px" whiteSpace="pre-wrap">{q.notes || ""}</Td>
                    <Td>{formatDate(q.stampedDate)}</Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </Box>

      {canQuote && (
        <Box borderWidth="1px" borderRadius="md" p={4} bg="whiteAlpha.800">
          <Heading size="sm" mb={1}>{quotes.length ? "Send another quote" : "Send a quote"}</Heading>
          {/* The API sets the quote's organisation from the request, so there's nothing to choose */}
          <Text fontSize="sm" color="gray.600" mb={3}>
            Quoting as <Text as="span" fontWeight="semibold"><PartyName partyId={quoteRequest.receiver?.partyId} /></Text>
            <Text as="span" fontSize="xs" color="gray.500"> · shares pre-filled from the request; change them if you need to</Text>
          </Text>
          <SimpleGrid columns={[1, 3]} spacing={3}>
            <FormControl>
              <FormLabel fontSize="xs">Quoted share %</FormLabel>
              <Input size="sm" type="number" min={0} max={100} step="any" value={form.quotedShare} onChange={set("quotedShare")} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Minimum share %</FormLabel>
              <Input size="sm" type="number" min={0} max={100} step="any" value={form.minimumShare} onChange={set("minimumShare")} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Maximum share %</FormLabel>
              <Input size="sm" type="number" min={0} max={100} step="any" value={form.maximumShare} onChange={set("maximumShare")} />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="xs">Valid until</FormLabel>
              <Input size="sm" type="datetime-local" value={form.validUntilDate} onChange={set("validUntilDate")} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Your reference</FormLabel>
              <Input size="sm" value={form.reference} onChange={set("reference")} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Stamp reference</FormLabel>
              <Input size="sm" placeholder="Syndicate / company stamp" value={form.stampReference} onChange={set("stampReference")} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Participation basis</FormLabel>
              <Input size="sm" placeholder={terms.participationBasis || "As requested"} value={form.participationBasis} onChange={set("participationBasis")} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Currency</FormLabel>
              <Input size="sm" placeholder={terms.currency || "As requested"} value={form.currency} onChange={set("currency")} />
            </FormControl>
          </SimpleGrid>
          <NotesField
            mt={3}
            label="Quote detail"
            rows={10}
            value={form.notes}
            onChange={(notes) => setForm((f) => ({ ...f, notes }))}
            placeholder="Terms, rate, conditions: the detail of your quote"
          />
          <FormControl mt={3}>
            <FormLabel fontSize="xs">Subjectivities</FormLabel>
            <Textarea size="sm" value={form.subjectivities} onChange={set("subjectivities")} />
          </FormControl>
          <Text fontSize="xs" color="gray.500" mt={2}>
            Give a quoted share, or a minimum and maximum share (or both).
          </Text>
          <HStack mt={3}>
            <Button size="sm" colorScheme="blue" onClick={stampAndSend} isLoading={sending}>
              Stamp and send quote
            </Button>
          </HStack>
        </Box>
      )}

      {canDecline && (
        <Box borderWidth="1px" borderRadius="md" p={4} bg="whiteAlpha.800">
          <Heading size="sm" mb={3}>Decline to quote</Heading>
          <ReasonSelect isRequired value={reasonCode} onChange={setReasonCode} mb={3} />
          <FormControl isRequired={isOther(reasonCode)}>
            <FormLabel fontSize="xs">{isOther(reasonCode) ? "Explain the reason" : "More detail (optional)"}</FormLabel>
            <Textarea size="sm" value={reason} onChange={(e) => setReason(e.target.value)} />
          </FormControl>
          <HStack mt={3}>
            <Button size="sm" colorScheme="red" onClick={decline} isLoading={declining}>Decline to quote</Button>
          </HStack>
        </Box>
      )}
    </VStack>
  );
};

export default CarrierQuoteRequestView;
